import type { FC } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip, Tooltip } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { GroupTrafficShare } from "app/components/React/Table/cells";
import { DEFAULT_DECIMAL, FORMATTERS, PERCENTAGE } from "app/common/benchmark/formatters";

import { computeYoY, formatVisits } from "../../helpers";

import { ShareValue, VisitsShareContainer, VisitsValue } from "./styledComponent";

interface LobTrafficShareTooltipProps {
    trafficShare: Record<string, number>;
    totalVisits: { visits: number };
    domainYoYChanges?: Record<string, { currentPeriodVisits: number; comparePeriodVisits: number }>;
    visibleDomains?: Set<string>;
}

const StyledTooltip = styled(Tooltip)`
    && {
        background-color: ${colorsPalettes.carbon[0]};
        border-radius: 3px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        min-width: 368px !important;
        padding: 16px;
    }
`;
const GroupTrafficShareStyle = { display: "flex", alignItems: "center" };

interface TooltipRow {
    domain: { label: string; background: string };
    visitsShare: { formattedVisits: string; formattedShare: string };
    yoyChange: { value: string | null; isDecrease: boolean | null; unsigned: boolean };
}

const COLUMN_DEFINITION = [
    {
        key: "1",
        title: "Domain",
        selector: "domain" as const,
        align: "left" as const,
        borderRight: false,
        size: ChartTooltip.LabelCellDefaultWidth,
        render: ({ domain }: { domain: { label: string; background: string } }) => (
            <ChartTooltip.LabelCell
                label={domain.label}
                backgroundColor={domain.background}
            />
        ),
    },
    {
        key: "2",
        title: "Visits & Share",
        selector: "visitsShare" as const,
        align: "right" as const,
        borderRight: true,
        size: "108px",
        render: ({ visitsShare }: { visitsShare: { formattedVisits: string; formattedShare: string } }) => (
            <VisitsShareContainer>
                <VisitsValue>{visitsShare.formattedVisits}</VisitsValue>
                <ShareValue>{visitsShare.formattedShare}</ShareValue>
            </VisitsShareContainer>
        ),
    },
    {
        key: "3",
        title: "YoY Change",
        selector: "yoyChange" as const,
        align: "right" as const,
        size: ChartTooltip.ChangeCellDefaultWidth,
        render: ({
            yoyChange,
        }: {
            yoyChange: { value: string | null; isDecrease: boolean | null; unsigned: boolean };
        }) => (
            <ChartTooltip.ChangeCell
                value={yoyChange.value || ""}
                isDecrease={yoyChange.isDecrease ?? undefined}
                unsigned={yoyChange.unsigned}
            />
        ),
    },
];

const TooltipContent: FC<LobTrafficShareTooltipProps> = ({
    trafficShare,
    totalVisits,
    domainYoYChanges,
    visibleDomains,
}) => {
    const rows = useMemo(() => {
        const domains = Object.keys(trafficShare || {});
        if (!domains.length) {
            return [];
        }

        const lobTotalVisits = totalVisits?.visits || 0;

        return domains
            .filter((domain) => !visibleDomains || visibleDomains.has(domain)) // Only show visible domains
            .map((domain) => {
                const domainShare = trafficShare[domain];
                const domainVisitsInLob = lobTotalVisits * domainShare;
                const formattedVisits = formatVisits(domainVisitsInLob);
                const formattedShare = `${FORMATTERS.percent(domainShare, DEFAULT_DECIMAL)}${PERCENTAGE}`;

                const domainYoYChange = domainYoYChanges?.[domain];

                const { label, isDecrease, unsigned } = domainYoYChange
                    ? computeYoY(domainYoYChange.currentPeriodVisits, domainYoYChange.comparePeriodVisits)
                    : { label: "-", isDecrease: null, unsigned: true };

                const color = chosenSites.getSiteColor(domain);

                return {
                    domain: { label: domain, background: color },
                    visitsShare: { formattedVisits, formattedShare },
                    yoyChange: { value: label, isDecrease, unsigned },
                };
            });
    }, [trafficShare, totalVisits, domainYoYChanges, visibleDomains]);

    if (!rows.length) {
        return null;
    }

    return (
        <ChartTooltip<TooltipRow>
            width={368}
            title="Traffic Share"
            columnDefinition={COLUMN_DEFINITION}
            data={rows}
        />
    );
};

const LobGroupTrafficShare: FC<LobTrafficShareTooltipProps> = (props) => (
    <StyledTooltip
        placement="top"
        content={<TooltipContent {...props} />}
    >
        <GroupTrafficShare
            value={props.trafficShare}
            showTooltip={false}
            height={16}
            style={GroupTrafficShareStyle}
        />
    </StyledTooltip>
);

export default LobGroupTrafficShare;
